'use client';

import { createContext, useContext, useEffect } from 'react';
import { TypedEventBus } from '@/lib/events/event-bus';
import type { ApplicationEventMap } from '@/lib/events/types';
import { eventBus } from '@/lib/events/index';

const EventBusContext = createContext<TypedEventBus<ApplicationEventMap>>(eventBus);

export const useEventBus = () => useContext(EventBusContext);

export function EventBusProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Setup global error handling for uncaught events
    const errorHandler = (error: ErrorEvent) => {
      eventBus.publish('error:occurred', {
        message: error.message,
        code: 'UNCAUGHT_ERROR'
      }, 'window')
    };

    // Setup network error handling
    const networkHandler = () => {
      eventBus.publish('error:occurred', {
        message: 'Network connection lost',
        code: 'NETWORK_ERROR'
      }, 'window')
    };

    // Add global event listeners
    window.addEventListener('error', errorHandler)
    window.addEventListener('offline', networkHandler)

    // Cleanup
    return () => {
      window.removeEventListener('error', errorHandler)
      window.removeEventListener('offline', networkHandler)
    }
  }, [])

  // Make the event bus available globally for direct access in components that can't use hooks
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__eventBus = eventBus;
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).__eventBus;
      }
    };
  }, []);

  return (
    <EventBusContext.Provider value={eventBus}>
      {children}
    </EventBusContext.Provider>
  );
} 